import React, { useState, useEffect, useRef } from "react";
import {
    Form,
    FormGroup,
    Label,
    Input,
    Button,
    Row,
    Col,
    FormFeedback,
    ListGroup,
    ListGroupItem,
} from "reactstrap";
import Session from "../auth/Session";

const CustomerForm = ({ customer, apiBaseUrl, onSuccess, onCancel }) => {
    const [formData, setFormData] = useState({
        id: 0,
        qbCustomerId: "",
        vendorNumber: "",
        name: "",
        address1: "",
        address2: "",
        city: "",
        state: "",
        postalCode: "",
        contactName: "",
        contactEmail: "",
        salesRepId: "",
    });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);

    // --- New: sales rep autocomplete ---
    const [userSearch, setUserSearch] = useState("");
    const [userList, setUserList] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [showUserList, setShowUserList] = useState(false);
    const dropdownRef = useRef(null);

    const usersApi = "/api/users"; // adjust if needed

    useEffect(() => {
        if (customer) setFormData(customer);
        else
            setFormData({
                id: 0,
                qbCustomerId: "",
                name: "",
                address1: "",
                address2: "",
                city: "",
                state: "",
                postalCode: "",
                contactName: "",
                contactEmail: "",
                salesRepId: "",
            });
    }, [customer]);

    // Fetch all users for autocomplete
    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const token = await Session.getToken();
                const res = await fetch(usersApi, { headers: { Authorization: `Bearer ${token}` } });
                if (res.ok) {
                    const data = await res.json();
                    setUserList(data);
                }
            } catch (err) {
                console.error("Failed to fetch users:", err);
            }
        };
        fetchUsers();
    }, []);

    // Filter user list as the user types
    useEffect(() => {
        if (userSearch.trim() === "") {
            setFilteredUsers([]);
        } else {
            const filtered = userList.filter(
                (u) =>
                    u.userName.toLowerCase().includes(userSearch.toLowerCase()) ||
                    `${u.firstName} ${u.lastName}`
                        .toLowerCase()
                        .includes(userSearch.toLowerCase())
            );
            setFilteredUsers(filtered);
        }
    }, [userSearch, userList]);

    // Hide dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setShowUserList(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const validate = () => {
        const newErrors = {};
        if (!formData.name) newErrors.name = "Customer name is required";
        if (!formData.contactEmail) newErrors.contactEmail = "Contact email required";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        setLoading(true);
        try {
            const data = {
                id: formData.id,
                qbCustomerId: formData.qbCustomerId,
                vendorNumber: formData.vendorNumber,
                name: formData.name,
                address1: formData.address1,
                address2: formData.address2,
                city: formData.city,
                state: formData.state,
                postalCode: formData.postalCode,
                contactName: formData.contactName,
                contactEmail: formData.contactEmail,
                salesRepId: formData.salesRepId,
            }
            const method = customer ? "PUT" : "POST";
            const url = customer ? `${apiBaseUrl}/${formData.id}` : apiBaseUrl;
            const res = await fetch(url, {
                method,
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(data),
            });

            if (!res.ok) throw new Error("Failed to save customer");
            const result = await res.json();
            onSuccess(result);
        } catch (err) {
            alert(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleSelectUser = (user) => {
        setFormData((prev) => ({ ...prev, salesRepId: user.id }));
        setUserSearch(`${user.firstName} ${user.lastName} (${user.userName})`);
        setShowUserList(false);
    };

    return (
        <Form onSubmit={handleSubmit}>
            <Row>
                <Col md={6}>
                    <FormGroup>
                        <Label>Customer Name</Label>
                        <Input
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            invalid={!!errors.name}
                        />
                        <FormFeedback>{errors.name}</FormFeedback>
                    </FormGroup>
                </Col>
                <Col md={6}>
                    <FormGroup>
                        <Label>QBCustomerId</Label>
                        <Input
                            name="qbCustomerId"
                            value={formData.qbCustomerId}
                            onChange={handleChange}
                        />
                    </FormGroup>
                </Col>
            </Row>

            <Row>
                <Col md={6}>
                    <FormGroup>
                        <Label>Vendor / Account #</Label>
                        <Input
                            name="vendorNumber"
                            value={formData.vendorNumber ?? ""}
                            onChange={handleChange}
                            placeholder="e.g. WorldPac vendor number"
                        />
                    </FormGroup>
                </Col>
            </Row>

            <Row>
                <Col md={8}>
                    <FormGroup>
                        <Label>Address 1</Label>
                        <Input
                            name="address1"
                            value={formData.address1}
                            onChange={handleChange}
                        />
                    </FormGroup>
                </Col>
                <Col md={4}>
                    <FormGroup>
                        <Label>Address 2</Label>
                        <Input
                            name="address2"
                            value={formData.address2}
                            onChange={handleChange}
                        />
                    </FormGroup>
                </Col>
            </Row>

            <Row>
                <Col md={4}>
                    <FormGroup>
                        <Label>City</Label>
                        <Input name="city" value={formData.city} onChange={handleChange} />
                    </FormGroup>
                </Col>
                <Col md={4}>
                    <FormGroup>
                        <Label>State</Label>
                        <Input name="state" value={formData.state} onChange={handleChange} />
                    </FormGroup>
                </Col>
                <Col md={4}>
                    <FormGroup>
                        <Label>Postal Code</Label>
                        <Input
                            name="postalCode"
                            value={formData.postalCode}
                            onChange={handleChange}
                        />
                    </FormGroup>
                </Col>
            </Row>

            <Row>
                <Col md={6}>
                    <FormGroup>
                        <Label>Contact Name</Label>
                        <Input
                            name="contactName"
                            value={formData.contactName}
                            onChange={handleChange}
                        />
                    </FormGroup>
                </Col>
                <Col md={6}>
                    <FormGroup>
                        <Label>Contact Email</Label>
                        <Input
                            type="email"
                            name="contactEmail"
                            value={formData.contactEmail}
                            onChange={handleChange}
                            invalid={!!errors.contactEmail}
                        />
                        <FormFeedback>{errors.contactEmail}</FormFeedback>
                    </FormGroup>
                </Col>
            </Row>

            {/* SalesRepId Autocomplete */}
            <FormGroup ref={dropdownRef} className="position-relative">
                <Label>Sales Representative</Label>
                <Input
                    type="text"
                    placeholder="Search by name or username..."
                    value={userSearch}
                    onFocus={() => setShowUserList(true)}
                    onChange={(e) => {
                        setUserSearch(e.target.value);
                        setShowUserList(true);
                    }}
                />
                {showUserList && filteredUsers.length > 0 && (
                    <ListGroup
                        className="position-absolute w-100"
                        style={{
                            zIndex: 1000,
                            maxHeight: "200px",
                            overflowY: "auto",
                        }}
                    >
                        {filteredUsers.map((u) => (
                            <ListGroupItem
                                key={u.id}
                                action
                                onClick={() => handleSelectUser(u)}
                            >
                                {u.firstName} {u.lastName} — ({u.userName})
                            </ListGroupItem>
                        ))}
                    </ListGroup>
                )}
            </FormGroup>


            <div className="d-flex justify-content-end gap-2">
                <Button color="secondary" onClick={onCancel} disabled={loading}>
                    Cancel
                </Button>
                <Button color="primary" type="submit" disabled={loading}>
                    {loading ? "Saving..." : customer ? "Update" : "Create"}
                </Button>
            </div>
        </Form>
    );
};

export default CustomerForm;
